// ============================================================================
// Congruences (moteur v3) : la feuille de brouillon en arithmétique modulaire.
// ============================================================================
// Notation du lycée (maths expertes) : « 2¹⁰⁰ = (2³)^33 × 2 ≡ 1^33 × 2 ≡ 2 [7] ».
// Une ligne est une CHAÎNE de membres reliés par « = » ou « ≡ », le modulo à la
// fin entre crochets. Chaque maillon est jugé :
//   « = » : égalité exacte (calculée en entiers exacts, même pour 2¹⁰⁰) ;
//   « ≡ » : congruence vraie ET pas justifié — réduire un nombre (petit), retirer
//           un multiple du modulo, remplacer par un résultat déjà établi,
//           élever une congruence à une puissance (exposant inchangé), petit
//           théorème de Fermat. Un produit ou une puissance se CALCULE d'abord
//           avec « = » : « 2¹⁰⁰ ≡ 2 [7] » ou « 5 × 9 ≡ 1 [11] » d'un bond sont refusés.
// Avec une inconnue (« 3x ≡ 1 [5] »), une ligne découle d'une congruence déjà
// écrite : on multiplie les deux membres, puis on réduit chaque membre.
//
// Le but : la cible (`congruence.target`, une expression ou l'inconnue) est
// congrue à un reste écrit entre 0 et n − 1.
//
// Le corrigé (`congruence.path`) est fourni par le modèle ; la porte de
// validation le rejoue ligne à ligne avec ce juge (rien n'est accepté sans lui).
// Ce mode est séparé du moteur des égalités : il ne change rien aux autres feuilles.
// ============================================================================

import { normalizeChips, checkSyntax, type Chip } from './lexer';
import type { ScratchModel } from './model';
import {
  canon,
  ev,
  close,
  lineAtoms,
  lineText,
  nary,
  neg,
  num,
  parseLine,
  parseText,
  pw,
  symsOf,
  textOf,
  type Eq,
  type Line,
  type Node,
} from './tree';

export interface CongSpec {
  mod: number;
  target: string;
  residue?: number;
  path: string[];
}

export interface CongFact {
  eq: Eq;
  source: 'given' | 'line';
}

export interface CongState {
  n: number;
  N: bigint;
  target: Node;
  /** reste attendu, entre 0 et n − 1 */
  residue: number;
  /** grandeurs inconnues : représentants de leur classe (x ≡ 2 [5] → 2, 7) */
  reps: Record<string, number[]>;
  parent: Map<string, string>;
  nodes: Map<string, Node>;
  facts: CongFact[];
}

/** Ce que le juge renvoie (même forme que `Verdict` du moteur des égalités). */
export interface CongVerdict {
  kind:
    | 'empty'
    | 'syntax'
    | 'trap'
    | 'open'
    | 'pending'
    | 'step'
    | 'false'
    | 'unjustified'
    | 'useless';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  hora?: number;
  final?: boolean;
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  trap?: { line: Line; title: string; why: string };
  /**
   * noyau (moteur v34) : la ligne est JUSTE mais ne se justifie pas encore en un pas.
   * L'ancien moteur la refuse ; le noyau la garde, sans HORA (D7).
   */
  keep?: { line: Line; kind: 'jump' | 'aside' };
}

// ── entiers exacts ─────────────────────────────────────────────────────────

const MAX_BITS = 32768;

function bigOfNumber(v: number): bigint | null {
  return Number.isInteger(v) && Math.abs(v) <= Number.MAX_SAFE_INTEGER ? BigInt(v) : null;
}
function bitLen(b: bigint): number {
  return (b < 0n ? -b : b).toString(2).length;
}

/** Valeur entière exacte (null : pas un entier, trop grande, ou inconnue). */
export function bigVal(t: Node, env: Record<string, bigint>): bigint | null {
  switch (t.k) {
    case 'n':
      return bigOfNumber(t.v!);
    case 's':
      return env[t.n!] ?? null;
    case 'add': {
      let s = 0n;
      for (const c of t.c!) {
        const v = bigVal(c, env);
        if (v === null) return null;
        s += v;
      }
      return s;
    }
    case 'mul': {
      let p = 1n;
      for (const c of t.c!) {
        const v = bigVal(c, env);
        if (v === null) return null;
        p *= v;
        if (bitLen(p) > MAX_BITS) return null;
      }
      return p;
    }
    case 'neg': {
      const v = bigVal(t.a!, env);
      return v === null ? null : -v;
    }
    case 'sq': {
      const v = bigVal(t.a!, env);
      return v === null ? null : v * v;
    }
    case 'pow': {
      const b = bigVal(t.a!, env);
      const e = bigVal(t.b!, env);
      if (b === null || e === null || e < 0n) return null;
      if (b === 0n || b === 1n || b === -1n) return e === 0n ? 1n : b === -1n && e % 2n ? -1n : b;
      if (Number(e) * bitLen(b) > MAX_BITS) return null;
      return b ** e;
    }
    case 'div': {
      const a = bigVal(t.a!, env);
      const b = bigVal(t.b!, env);
      if (a === null || b === null || b === 0n || a % b !== 0n) return null;
      return a / b;
    }
    default:
      return null;
  }
}

function mod(a: bigint, N: bigint): bigint {
  const r = a % N;
  return r < 0n ? r + N : r;
}
function modPow(b: bigint, e: bigint, N: bigint): bigint {
  let r = 1n % N;
  let x = mod(b, N);
  let k = e;
  while (k > 0n) {
    if (k & 1n) r = (r * x) % N;
    x = (x * x) % N;
    k >>= 1n;
  }
  return r;
}

/** Reste modulo N (null : pas une expression entière). */
export function modVal(t: Node, N: bigint, env: Record<string, bigint>): bigint | null {
  switch (t.k) {
    case 'n': {
      const v = bigOfNumber(t.v!);
      return v === null ? null : mod(v, N);
    }
    case 's': {
      const v = env[t.n!];
      return v === undefined ? null : mod(v, N);
    }
    case 'add': {
      let s = 0n;
      for (const c of t.c!) {
        const v = modVal(c, N, env);
        if (v === null) return null;
        s = (s + v) % N;
      }
      return s;
    }
    case 'mul': {
      let p = 1n % N;
      for (const c of t.c!) {
        const v = modVal(c, N, env);
        if (v === null) return null;
        p = (p * v) % N;
      }
      return p;
    }
    case 'neg': {
      const v = modVal(t.a!, N, env);
      return v === null ? null : mod(-v, N);
    }
    case 'sq': {
      const v = modVal(t.a!, N, env);
      return v === null ? null : (v * v) % N;
    }
    case 'pow': {
      const b = modVal(t.a!, N, env);
      const e = bigVal(t.b!, env);
      if (b === null || e === null || e < 0n) return null;
      return modPow(b, e, N);
    }
    case 'div': {
      const v = bigVal(t, env);
      return v === null ? null : mod(v, N);
    }
    default:
      return null;
  }
}

// ── formes : rangement, facteurs ───────────────────────────────────────────

/** Forme rangée : produits et sommes aplatis, « × 1 » retiré, x² = x^2. */
export function tidy(t: Node): Node {
  if (t.k === 'n' || t.k === 's') return t;
  if (t.k === 'sq') return pw(tidy(t.a!), num(2));
  if (t.k === 'neg') return neg(tidy(t.a!));
  if (t.k === 'pow') return pw(tidy(t.a!), tidy(t.b!));
  if (t.k === 'mul') {
    const c = nary('mul', t.c!.map(tidy));
    if (c.k !== 'mul') return c;
    const kept = c.c!.filter((x) => !(x.k === 'n' && x.v === 1));
    if (!kept.length) return num(1);
    return kept.length === 1 ? kept[0]! : { k: 'mul', c: kept };
  }
  if (t.k === 'add') return nary('add', t.c!.map(tidy));
  if (t.k === 'div') return { k: 'div', a: tidy(t.a!), b: tidy(t.b!) };
  return t;
}
function key(t: Node): string {
  return canon(tidy(t));
}
function factors(t: Node): Node[] {
  const u = tidy(t);
  return u.k === 'mul' ? u.c!.slice() : [u];
}
function closed(t: Node): boolean {
  return Object.keys(symsOf(t)).length === 0;
}
function isPrime(p: number): boolean {
  if (p < 2 || !Number.isInteger(p)) return false;
  for (let d = 2; d * d <= p; d++) if (p % d === 0) return false;
  return true;
}

// ── classes de congruence établies (union-find sur les écritures) ─────────

function find(cs: CongState, k: string): string {
  let r = k;
  while (cs.parent.has(r) && cs.parent.get(r) !== r) r = cs.parent.get(r)!;
  return r;
}
function sameClass(cs: CongState, a: Node, b: Node): boolean {
  const ka = key(a);
  const kb = key(b);
  return ka === kb || (cs.parent.has(ka) && cs.parent.has(kb) && find(cs, ka) === find(cs, kb));
}
function union(cs: CongState, a: Node, b: Node): boolean {
  const ka = key(a);
  const kb = key(b);
  [
    [ka, a],
    [kb, b],
  ].forEach(([k, n]) => {
    if (!cs.parent.has(k as string)) {
      cs.parent.set(k as string, k as string);
      cs.nodes.set(k as string, tidy(n as Node));
    }
  });
  const ra = find(cs, ka);
  const rb = find(cs, kb);
  if (ra === rb) return false;
  cs.parent.set(ra, rb);
  return true;
}
function cloneClasses(cs: CongState): CongState {
  return { ...cs, parent: new Map(cs.parent), nodes: new Map(cs.nodes), facts: cs.facts.slice() };
}

// ── vérité ─────────────────────────────────────────────────────────────────

/** valeurs d'essai d'une identité « = » avec une inconnue (pour tout x) */
const SAMPLE_VALUES = [-4, 3, 11, 17];

function envs(cs: CongState, t: Node[], generic: boolean): Record<string, bigint>[] {
  const names = Object.keys(symsOf({ k: 'add', c: t }));
  let out: Record<string, bigint>[] = [{}];
  for (const n of names) {
    const vals = generic ? SAMPLE_VALUES : (cs.reps[n] ?? []);
    if (!vals.length) return [];
    const next: Record<string, bigint>[] = [];
    out.forEach((e) => vals.forEach((v) => next.push({ ...e, [n]: BigInt(v) })));
    out = next;
  }
  return out;
}

function equalTrue(cs: CongState, a: Node, b: Node): boolean {
  const list = envs(cs, [a, b], true);
  if (!list.length) return false;
  return list.every((e) => {
    const x = bigVal(a, e);
    const y = bigVal(b, e);
    if (x !== null && y !== null) return x === y;
    const fe: Record<string, number> = {};
    Object.keys(e).forEach((k) => (fe[k] = Number(e[k])));
    return close(ev(a, fe), ev(b, fe));
  });
}
function congTrue(cs: CongState, a: Node, b: Node): boolean | null {
  const list = envs(cs, [a, b], false);
  if (!list.length) return false;
  let ok = true;
  for (const e of list) {
    const x = modVal(a, cs.N, e);
    const y = modVal(b, cs.N, e);
    if (x === null || y === null) return null;
    if (x !== y) ok = false;
  }
  return ok;
}
/** reste d'une expression sans inconnue (texte), ou null */
function residueText(cs: CongState, t: Node): string | null {
  if (!closed(t)) return null;
  const r = modVal(t, cs.N, {});
  return r === null ? null : r.toString();
}

function eqTrue(cs: CongState, eq: Eq): { ok: boolean; at: number; impossible?: boolean } {
  for (let i = 0; i + 1 < eq.sides.length; i++) {
    const rel = eq.rels ? eq.rels[i] : '=';
    const a = eq.sides[i]!;
    const b = eq.sides[i + 1]!;
    if (rel === '=') {
      if (!equalTrue(cs, a, b)) return { ok: false, at: i };
    } else {
      const t = congTrue(cs, a, b);
      if (t === null) return { ok: false, at: i, impossible: true };
      if (!t) return { ok: false, at: i };
    }
  }
  return { ok: true, at: -1 };
}

// ── pas justifiés ──────────────────────────────────────────────────────────

/** borne du calcul de tête : un nombre plus grand se divise d'abord (27 = 2 × 11 + 5) */
function mental(cs: CongState): bigint {
  return BigInt(Math.max(100, 10 * cs.n));
}
function smallInt(cs: CongState, t: Node): bigint | null {
  if (t.k !== 'n' && !(t.k === 'neg' && t.a!.k === 'n')) return null;
  const v = bigVal(t, {});
  if (v === null) return null;
  const b = mental(cs);
  return v <= b && v >= -b ? v : null;
}
/** un multiple du modulo écrit comme tel : 2 × 11, 11k, 22 */
function multipleOfN(cs: CongState, t: Node): boolean {
  const u = tidy(t.k === 'neg' ? t.a! : t);
  if (u.k === 'n') {
    const v = bigVal(u, {});
    return v !== null && v !== 0n && v % cs.N === 0n;
  }
  if (u.k === 'mul')
    return u.c!.some((f) => {
      if (f.k !== 'n') return false;
      const v = bigVal(f, {});
      return v !== null && v % cs.N === 0n;
    });
  return false;
}

const L_REDUCE = 'Réduction modulo ';
const L_SUBST = 'Remplacement par une congruence établie';
const L_POW = 'Puissance d’une congruence';
const L_FERMAT = 'Petit théorème de Fermat';

/** A ≡ B se justifie-t-il en un pas ? Renvoie le nom du pas, '' si identique, null sinon. */
function congStep(cs: CongState, A0: Node, B0: Node, depth = 0): string | null {
  if (depth > 12) return null;
  const A = tidy(A0);
  const B = tidy(B0);
  if (canon(A) === canon(B)) return '';
  if (sameClass(cs, A, B)) return L_SUBST;
  const red = L_REDUCE + cs.n;
  // 27 ≡ 5 [11] : un nombre de tête, remplacé par un nombre congru
  const a = smallInt(cs, A);
  const b = smallInt(cs, B);
  if (a !== null && b !== null) return mod(a - b, cs.N) === 0n ? red : null;
  // petit théorème de Fermat : a^(p−1) ≡ 1 [p], a^p ≡ a [p]
  if (isPrime(cs.n) && A.k === 'pow' && A.a!.k === 'n' && A.b!.k === 'n') {
    const base = bigVal(A.a!, {});
    const e = A.b!.v!;
    if (base !== null && mod(base, cs.N) !== 0n) {
      if (e === cs.n - 1 && B.k === 'n' && B.v === 1) return L_FERMAT;
      if (e === cs.n && canon(B) === canon(A.a!)) return L_FERMAT;
    }
  }
  if (A.k === 'neg' && B.k === 'neg') return congStep(cs, A.a!, B.a!, depth + 1);
  // 2 × 3 ≡ 0 [3] : un multiple du modulo écrit comme tel
  if (B.k === 'n' && B.v === 0 && multipleOfN(cs, A)) return red;
  // 4 × 11 + 1 ≡ 1 [11] : on retire un multiple du modulo
  if (A.k === 'add') {
    const keep = A.c!.filter((t) => !multipleOfN(cs, t));
    if (keep.length < A.c!.length) {
      const rest = keep.length ? nary('add', keep) : num(0);
      if (congStep(cs, rest, B, depth + 1) !== null) return red;
    }
  }
  // (2³)^33 ≡ 1^33 : on élève une congruence à une puissance (exposant inchangé)
  if (A.k === 'pow' && B.k === 'pow' && canon(tidy(A.b!)) === canon(tidy(B.b!))) {
    const s = congStep(cs, A.a!, B.a!, depth + 1);
    if (s !== null) return s === '' ? '' : L_POW;
  }
  // sommes : membre à membre
  if (A.k === 'add' && B.k === 'add' && A.c!.length === B.c!.length) {
    const r = pairUp(cs, A.c!, B.c!, depth);
    if (r !== null) return r;
  }
  // produits : facteur à facteur, ou les nombres d'un produit qui portent l'inconnue
  if (A.k === 'mul' || B.k === 'mul') {
    const fa = factors(A);
    const fb = factors(B);
    if (fa.length === fb.length) {
      const r = pairUp(cs, fa, fb, depth);
      if (r !== null) return r;
    }
    const na = fa.filter((f) => !closed(f));
    const nb = fb.filter((f) => !closed(f));
    if (nb.length && na.length === nb.length && pairUp(cs, na, nb, depth) !== null) {
      // 9 × 5x ≡ x [11] : 9 × 5 = 45 ≡ 1, calculable de tête
      const ca = fa.filter(closed);
      const cb = fb.filter(closed);
      const pa = bigVal(nary('mul', ca.length ? ca : [num(1)]), {});
      const pb = bigVal(nary('mul', cb.length ? cb : [num(1)]), {});
      const m = mental(cs);
      if (pa !== null && pb !== null && pa <= m && pa >= -m && mod(pa - pb, cs.N) === 0n)
        return red;
    }
  }
  return null;
}
function pairUp(cs: CongState, xs: Node[], ys: Node[], depth: number): string | null {
  const used = new Set<number>();
  const labels: string[] = [];
  for (const x of xs) {
    let found = -1;
    for (let j = 0; j < ys.length && found < 0; j++) {
      if (used.has(j)) continue;
      const s = congStep(cs, x, ys[j]!, depth + 1);
      if (s !== null) {
        found = j;
        if (s) labels.push(s);
      }
    }
    if (found < 0) return null;
    used.add(found);
  }
  return labels.length ? labels[0]! : '';
}

/** réécriture d'un membre : identique, égal (identité), ou un pas de congruence */
function rewrite(cs: CongState, from: Node, to: Node): boolean {
  if (key(from) === key(to)) return true;
  if (equalTrue(cs, from, to)) return true;
  return congStep(cs, from, to) !== null;
}

/** la congruence S0 ≡ S1 (avec une inconnue) découle-t-elle d'une congruence déjà écrite ? */
function derived(cs: CongState, s0: Node, s1: Node): string | null {
  for (const f of cs.facts) {
    const sides = f.eq.sides;
    const P = sides[0]!;
    const Q = sides[sides.length - 1]!;
    for (const [p, q] of [
      [P, Q],
      [Q, P],
    ] as [Node, Node][]) {
      // on multiplie les deux membres par le même nombre : 2 × 3x ≡ 2 × 1
      const f0 = tidy(s0);
      const f1 = tidy(s1);
      if (f0.k === 'mul' && f0.c![0]!.k === 'n') {
        const k = f0.c![0]!;
        if (
          k.v !== 1 &&
          equalTrue(cs, f0, nary('mul', [k, p])) &&
          equalTrue(cs, f1, nary('mul', [k, q]))
        )
          return 'Multiplier les deux membres par ' + textOf(k);
      }
      // on réduit / calcule chaque membre
      if (rewrite(cs, p, s0) && rewrite(cs, q, s1)) return 'Réduction modulo ' + cs.n;
    }
  }
  return null;
}

// ── état ───────────────────────────────────────────────────────────────────

export function createCongState(model: ScratchModel, spec: CongSpec): CongState {
  const reps: Record<string, number[]> = {};
  Object.entries(model.symbols).forEach(([n, s]) => {
    const vals = s.given && s.value !== undefined ? [s.value] : (s.values ?? []);
    if (vals.length) reps[n] = vals.flatMap((v) => [v, v + spec.mod]);
  });
  const target = parseText(spec.target).dis[0]!.sides[0]!;
  const cs: CongState = {
    n: spec.mod,
    N: BigInt(spec.mod),
    target,
    residue: 0,
    reps,
    parent: new Map(),
    nodes: new Map(),
    facts: [],
  };
  let r: number | null = spec.residue ?? null;
  if (r === null) {
    // reste de la cible pour les représentants des inconnues (a ≡ 3, b ≡ 5 → a × b ≡ 1)
    const e = envs(cs, [target], false)[0];
    const v = e ? modVal(target, cs.N, e) : null;
    r = v === null ? 0 : Number(v);
  }
  cs.residue = r;
  (model.givens || []).forEach((g) => addCongFact(cs, parseText(g).dis[0]!, 'given'));
  (model.admitted || []).forEach((g) => addCongFact(cs, parseText(g).dis[0]!, 'given'));
  return cs;
}

export function addCongFact(cs: CongState, eq: Eq, source: 'given' | 'line'): number {
  let merged = 0;
  for (let i = 0; i + 1 < eq.sides.length; i++)
    if (union(cs, eq.sides[i]!, eq.sides[i + 1]!)) merged++;
  cs.facts.push({ eq, source });
  return merged;
}

/** la cible est-elle congrue à un reste écrit (entre 0 et n − 1) ? */
export function congGoalReached(cs: CongState): boolean {
  const kt = key(cs.target);
  if (!cs.parent.has(kt)) return false;
  const root = find(cs, kt);
  for (const [k, n] of cs.nodes) {
    if (find(cs, k) !== root || n.k !== 'n') continue;
    if (Number.isInteger(n.v) && n.v! >= 0 && n.v! < cs.n && n.v === cs.residue) return true;
  }
  return false;
}

/** restes écrits pour la cible (réponses trouvées) */
export function congStatedResidues(cs: CongState): number[] {
  const kt = key(cs.target);
  if (!cs.parent.has(kt)) return [];
  const root = find(cs, kt);
  const out: number[] = [];
  cs.nodes.forEach((n, k) => {
    if (find(cs, k) === root && n.k === 'n' && Number.isInteger(n.v) && n.v! >= 0 && n.v! < cs.n)
      out.push(n.v!);
  });
  return out;
}

/** la ligne est-elle vraie (chaque maillon) ? */
export function congIsTrue(cs: CongState, line: Line): boolean {
  return line.dis.every((d) => eqTrue(cs, d).ok);
}

// ── juge ───────────────────────────────────────────────────────────────────

function chipAt(src: number[], i: number, n: number): number {
  return src[i] !== undefined ? src[i]! : n - 1;
}

export function congJudge(
  cs: CongState,
  model: ScratchModel,
  chips: Chip[],
  opts?: { verify?: boolean },
): CongVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const syn = checkSyntax(A);
  if (!syn.ok)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: chipAt(norm.src, syn.index, chips.length),
    };
  if (!syn.complete || !syn.hasEq) return { kind: 'open', atoms: A, complete: false };
  let E: Line;
  try {
    E = parseLine(A);
  } catch {
    return { kind: 'open', atoms: A, complete: false };
  }
  const v = evaluate(cs, model, E, A);
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

function trapFor(model: ScratchModel, E: Line): { line: Line; title: string; why: string } | null {
  const k = lineKey(E);
  for (const t of model.traps || []) {
    try {
      const tl = parseText(t.line);
      if (lineKey(tl) === k) return { line: tl, title: t.title, why: t.why };
    } catch {
      /* piège illisible : ignoré */
    }
  }
  return null;
}
function lineKey(L: Line): string {
  return L.dis
    .map((d) => key(d.sides[0]!) + '~' + key(d.sides[d.sides.length - 1]!) + '~' + (d.mod ?? ''))
    .join('|');
}

function evaluate(cs: CongState, model: ScratchModel, E: Line, A: string[]): CongVerdict {
  if (E.dis.length !== 1)
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Une congruence à la fois',
      message: 'Écris une seule congruence (ou une seule chaîne) par ligne.',
    };
  const eq = E.dis[0]!;
  const rels = eq.rels ?? eq.sides.slice(1).map(() => '=' as const);
  if (eq.mod !== undefined && eq.mod !== cs.n)
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Pas le bon modulo',
      message: `Dans cette question, on travaille modulo ${cs.n} : termine ta congruence par [${cs.n}].`,
    };

  const trap = trapFor(model, E);
  const truth = eqTrue(cs, eq);
  if (!truth.ok) {
    if (trap)
      return {
        kind: 'trap',
        atoms: A,
        title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
        message: trap.why,
        trap,
        badChip: -1,
      };
    const a = eq.sides[truth.at]!;
    const b = eq.sides[truth.at + 1]!;
    if (truth.impossible)
      return {
        kind: 'false',
        atoms: A,
        title: 'Pas dans les entiers',
        message:
          'Une congruence relie des ENTIERS : on ne divise jamais. Pour « diviser » par a, on multiplie par un inverse de a modulo ' +
          cs.n +
          '.',
      };
    if (rels[truth.at] === '=') {
      const ra = residueText(cs, a);
      const rb = residueText(cs, b);
      if (!(closed(a) && closed(b)) && congTrue(cs, a, b))
        return {
          kind: 'false',
          atoms: A,
          title: 'Ce n’est pas une égalité',
          message: `« ${textOf(a)} » et « ${textOf(b)} » ne sont pas égaux, seulement congrus : écris « ${textOf(a)} ≡ ${textOf(b)} [${cs.n}] ».`,
        };
      return {
        kind: 'false',
        atoms: A,
        title: 'Ce n’est pas égal',
        message:
          `« ${textOf(a)} = ${textOf(b)} » est faux.` +
          (ra !== null && rb !== null && ra === rb
            ? ` Ces deux nombres sont seulement CONGRUS modulo ${cs.n} : écris « ≡ », pas « = ».`
            : ''),
      };
    }
    const ra = residueText(cs, a);
    const rb = residueText(cs, b);
    return {
      kind: 'false',
      atoms: A,
      title: 'Ce n’est pas congru',
      message:
        `« ${textOf(a)} ≡ ${textOf(b)} [${cs.n}] » est faux` +
        (ra !== null && rb !== null
          ? ` : modulo ${cs.n}, ${textOf(a)} a pour reste ${ra} et ${textOf(b)} a pour reste ${rb}.`
          : '.'),
    };
  }

  // chaque maillon doit se justifier
  const labels: string[] = [];
  let hasFermat = false;
  for (let i = 0; i + 1 < eq.sides.length; i++) {
    const a = eq.sides[i]!;
    const b = eq.sides[i + 1]!;
    if (rels[i] === '=') {
      if (!labels.includes('Calcul')) labels.push('Calcul');
      continue;
    }
    let s = congStep(cs, a, b);
    if (s === null && i === 0 && !(closed(a) && closed(b))) s = derived(cs, a, b);
    if (s === null) {
      const big = closed(a) && (a.k !== 'n' || smallInt(cs, a) === null);
      return {
        kind: 'unjustified',
        atoms: A,
        keep: { line: E, kind: 'jump' },
        title: 'D’où vient ce maillon ?',
        message:
          `« ${textOf(a)} ≡ ${textOf(b)} [${cs.n}] » est vrai, mais il ne se justifie pas encore. ` +
          (big
            ? `Calcule d’abord ${textOf(a)} avec « = » (ou découpe la puissance), puis réduis modulo ${cs.n}.`
            : !closed(a)
              ? 'Pars d’une congruence déjà écrite : multiplie ses deux membres par un même nombre, puis réduis.'
              : 'Réduis un nombre, ou remplace par une congruence que tu as déjà établie.'),
      };
    }
    if (s === L_FERMAT) hasFermat = true;
    if (s && !labels.includes(s)) labels.push(s);
  }

  const before = congGoalReached(cs);
  const trial = cloneClasses(cs);
  const merged = addCongFact(trial, eq, 'line');
  if (!merged)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais ça ne t’avance pas',
      message: 'Chaque maillon est juste, mais tu avais déjà tout ça.',
    };
  const final = !before && congGoalReached(trial);
  const hora = Math.min(6, merged + (hasFermat ? 1 : 0));
  const label = labels.join(' → ') || 'Calcul';
  return {
    kind: 'step',
    atoms: A,
    line: E,
    final,
    hora,
    rule: {
      id: hasFermat ? 'fermat' : 'congruence',
      card: '',
      label,
      hora,
      why: final
        ? `Le reste est entre 0 et ${cs.n - 1} : c’est la réponse.`
        : 'Chaque maillon est juste et justifié.',
    },
  };
}

/** noyau (moteur v34) : la ligne, gardée, ferait-elle atteindre le but ? */
export function congWouldReach(cs: CongState, line: Line): boolean {
  if (congGoalReached(cs)) return false;
  const trial = cloneClasses(cs);
  line.dis.forEach((d) => addCongFact(trial, d, 'line'));
  return congGoalReached(trial);
}

// ── corrigé, coup de pouce ─────────────────────────────────────────────────

export function congPathLines(spec: CongSpec): Line[] {
  return spec.path.map((p) => parseText(p));
}

/** la ligne est-elle déjà établie (tous ses maillons dans une même classe) ? */
export function congEstablished(cs: CongState, L: Line): boolean {
  const d = L.dis[0]!;
  for (let i = 0; i + 1 < d.sides.length; i++)
    if (!sameClass(cs, d.sides[i]!, d.sides[i + 1]!)) return false;
  return true;
}

export function congLineText(L: Line): string {
  return lineText(L);
}
export function congLineAtoms(L: Line): string[] {
  return lineAtoms(L);
}
